import {
	Box,
	Button,
	Flex,
	useMediaQuery,
} from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { ChevronDownIcon } from '@/assets/icons';
import FaceliftMenu from '@/components/Atoms/FaceliftMenu';
import FaceliftSuccessProgressModal from '@/components/Molecules/FaceliftSuccessProgressModal';
import Show from '@/components/Molecules/Show';
import { useColorModeValue } from '@/components/ui/color-mode-utils';
import { useTopbarTutorial } from '@/hooks/tutorialsHooks/useTopbarTutorial';
import { useActiveNav } from '@/hooks/useActiveNav';
import { useAppTour } from '@/hooks/useAppTour';
import { useHoverMenu } from '@/hooks/useHoverMenu';
import usePermission from '@/hooks/usePermission';
import { useQuickActionsStore } from '@/store/quickActionsStore';
import { useTourContext } from '@/store/tourContext';
import { navigationItems } from '@/utils/navigationItems';
import {
	getNavItemPermission,
	getNavItemPermissionWithParent,
} from '@/utils/quickActionsUtils';

// Small inline icon component
const Chevron = ({ rotated = false }: { rotated?: boolean }) => {
	const iconColor = useColorModeValue('white', 'gray.100');
	return (
		<Box
			ml="2"
			display="inline-flex"
			alignItems="center"
			justifyContent="center"
			transition="transform 0.2s ease"
			transform={rotated ? 'rotate(180deg)' : undefined}
			mt="0.5"
		>
			<ChevronDownIcon width={10} height={5} color={iconColor} />
		</Box>
	);
};

interface NavLinksProps {
	isCollapsed?: boolean;
}

const NavLinks: React.FC<NavLinksProps> = ({ isCollapsed = false }) => {
	const navigate = useNavigate();

	// Theme-aware colors
	const textColor = useColorModeValue('white', 'gray.100');

	const { getActiveState } = useActiveNav();
	const { hasPermission } = usePermission();
	const { toggleQuickAction, isStarred } = useQuickActionsStore();

	const [isTogglingQuickAction, setIsTogglingQuickAction] = useState(false);
	const [isSucceed, setIsSucceed] = useState(false);
	const [hasError, setHasError] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string>('');

// Function to get button styles based on active state
const getButtonStyles = (item: typeof navigationItems[0]) => {
	const activeState = getActiveState(item);
	const baseStyles = {
		color: textColor,
		fontWeight: 600,
		fontSize: 'sm',
		variant: 'ghost' as const,
		bg: 'transparent',
		border: 'none',
		outline: 'none',
		boxShadow: 'none',
		_focus: { boxShadow: 'none', outline: 'none' },
		_focusVisible: { boxShadow: 'none', outline: 'none' },
		minW: isCollapsed ? '10' : 'auto',
		px: isCollapsed ? '2' : '3',
		transition: 'all 0.2s ease',
	};

	// Apply different styles based on active state
	switch (activeState) {
		case 'active':
			return {
				...baseStyles,
				bg: 'transparent',
				color: 'white',
				fontWeight: 'bold',
				textShadow: '0 0 12px rgba(255, 255, 255, 0.7)',
				transform: 'scale(1.05)',
				_hover: {
					textShadow: '0 0 16px rgba(255, 255, 255, 0.9)',
					transform: 'scale(1.08)',
				},
				_active: {
					transform: 'scale(1.02)', // Slight press effect
				},
				'& .nav-icon': {
					color: 'white',
					filter: 'drop-shadow(0 0 10px rgba(255, 255, 255, 0.7))',
					transform: 'scale(1.15)',
					transition: 'all 0.2s ease',
				},
			};
		case 'parent-active':
			return {
				...baseStyles,
				bg: 'transparent',
				color: 'white',
				fontWeight: 'semibold',
				textShadow: '0 0 8px rgba(255, 255, 255, 0.4)',
				_hover: {
					textShadow: '0 0 12px rgba(255, 255, 255, 0.6)',
				},
				'& .nav-icon': {
					color: 'white',
					filter: 'drop-shadow(0 0 6px rgba(255, 255, 255, 0.4))',
					transition: 'all 0.2s ease',
				},
			};
		default:
			return {
				...baseStyles,
				bg: 'transparent',
				color: textColor,
				_hover: { 
					color: 'white',
					textShadow: '0 0 8px rgba(255, 255, 255, 0.3)',
				},
				_active: { bg: 'transparent' },
				'& .nav-icon': {
					transition: 'all 0.2s ease',
				},
				'&:hover .nav-icon': {
					color: 'white',
					filter: 'drop-shadow(0 0 6px rgba(255, 255, 255, 0.3))',
					transform: 'scale(1.05)',
				},
			};
	}
};

// Function to get menu trigger styles based on active state
const getMenuTriggerStyles = (item: typeof navigationItems[0]) => {
	const activeState = getActiveState(item);
	const baseStyles = {
		display: 'inline-flex',
		alignItems: 'center',
		px: isCollapsed ? '2' : '3',
		py: '2',
		borderRadius: 'md',
		color: textColor,
		fontWeight: 600,
		fontSize: 'sm',
		cursor: 'pointer',
		transition: 'all 0.2s ease', // Changed to 0.2s for consistency
		minW: isCollapsed ? '10' : 'auto',
		bg: 'transparent',
		_hover: { bg: 'transparent' },
		_active: { bg: 'transparent' },
	};

	switch (activeState) {
		case 'active':
			return {
				...baseStyles,
				bg: 'transparent',
				color: 'white',
				fontWeight: 'bold',
				textShadow: '0 0 12px rgba(255, 255, 255, 0.7)',
				transform: 'scale(1.05)',
				_hover: {
					textShadow: '0 0 16px rgba(255, 255, 255, 0.9)',
					transform: 'scale(1.08)',
				},
				_active: {
					transform: 'scale(1.02)',
				},
				'& .nav-icon': {
					color: 'white',
					filter: 'drop-shadow(0 0 10px rgba(255, 255, 255, 0.7))',
					transform: 'scale(1.15)',
					transition: 'all 0.2s ease',
				},
				'& .chevron-icon': {
					color: 'white',
					filter: 'drop-shadow(0 0 8px rgba(255, 255, 255, 0.6))',
					transition: 'all 0.2s ease',
				},
			};
		case 'parent-active':
			return {
				...baseStyles,
				bg: 'transparent',
				color: 'white',
				fontWeight: 'semibold',
				textShadow: '0 0 8px rgba(255, 255, 255, 0.4)',
				_hover: {
					textShadow: '0 0 12px rgba(255, 255, 255, 0.6)',
				},
				'& .nav-icon': {
					color: 'white',
					filter: 'drop-shadow(0 0 6px rgba(255, 255, 255, 0.4))',
					transition: 'all 0.2s ease',
				},
				'& .chevron-icon': {
					color: 'white',
					filter: 'drop-shadow(0 0 4px rgba(255, 255, 255, 0.3))',
					transition: 'all 0.2s ease',
				},
			};
		default:
			return {
				...baseStyles,
				bg: 'transparent',
				color: textColor,
				_hover: { 
					color: 'white',
					textShadow: '0 0 8px rgba(255, 255, 255, 0.3)',
				},
				_active: { bg: 'transparent' },
				'& .nav-icon': {
					transition: 'all 0.2s ease',
				},
				'& .chevron-icon': {
					transition: 'all 0.2s ease',
				},
				'&:hover .nav-icon': {
					color: 'white',
					filter: 'drop-shadow(0 0 6px rgba(255, 255, 255, 0.3))',
					transform: 'scale(1.05)',
				},
				'&:hover .chevron-icon': {
					color: 'white',
					filter: 'drop-shadow(0 0 4px rgba(255, 255, 255, 0.2))',
				},
			};
	}
};

	const [openMenu, setOpenMenu] = useState<string | null>(null);
	const { cancelClose, scheduleClose } = useHoverMenu(250);
	const { isTourActive } = useTourContext();
	const authorizedMenuStateRef = useRef<string | null>(null);

	const setOpenMenuWithTutorialCheck = useCallback(
		(menu: string | null, forceClose = false) => {
			if (isTourActive && menu === null && !forceClose) {
				return;
			}
			authorizedMenuStateRef.current = menu;
			setOpenMenu(menu);
		},
		[isTourActive],
	);

	useEffect(() => {
		if (isTourActive) {
			if (openMenu !== authorizedMenuStateRef.current) {
				setOpenMenu(authorizedMenuStateRef.current);
			}
		} else {
			authorizedMenuStateRef.current = openMenu;
		}
	}, [openMenu, isTourActive]);

	// Navigation item refs - always tracked for tutorial registration
	const bvNavItemRef = useRef<HTMLButtonElement | null>(null);
	const ordersNavItemRef = useRef<HTMLElement | null>(null);
	const accountingFinanceNavItemRef = useRef<HTMLElement | null>(null);
	const invoicesNavItemRef = useRef<HTMLElement | null>(null);

	// Use the topbar tutorial hook to handle all tutorial logic
	const {
		handleSubmenuItemRef,
		setOrdersNavItemRef,
		setAccountingFinanceNavItemRef,
		setInvoicesNavItemRef,
		getTourElements,
		tourExpandSubmenu,
	} = useTopbarTutorial({
		cartButtonRef: { current: null },
		bvNavItemRef,
		ordersNavItemRef,
		accountingFinanceNavItemRef,
		invoicesNavItemRef,
		setOpenMenu: setOpenMenuWithTutorialCheck,
	});

	// Register elements with useAppTour - always register all navigation elements
	useAppTour({
		elements: getTourElements(),
	});

	// True between 1000px and 1200px
	const [isMidViewport] = useMediaQuery([
		'(min-width: 1030px) and (max-width: 1200px)',
	]);

	const renderTitle = (title: string) => {
		if (isCollapsed) {
			const nav = navigationItems.find(
				(n) => n.title.toLowerCase() === title.toLowerCase(),
			);
			return nav?.icon ?? null;
		}
		if (!isMidViewport) return title;
		const words = title.trim().split(/\s+/);
		if (words.length === 2) {
			return (
				<>
					{words[0]}
					<br />
					{words[1]}
				</>
			);
		}
		return title;
	};

	return (
		<Flex gap="1" align="center">
			{navigationItems.map((item) => {
				const handleNavigate = (href?: string) => {
					if (href) {
						// Close all menus immediately when navigating
						setOpenMenuWithTutorialCheck(null, true);
						navigate(href);
					}
				};

				if (
					item?.permissions &&
					item.permissions.length > 0 &&
					!hasPermission(item.permissions)
				) {
					return null;
				}

				const hasChildren = !!item.submenu?.length;
				const isBVNavItem = item.href === '/benefits-verifications';
				const buttonStyles = getButtonStyles(item);
				const menuTriggerStyles = getMenuTriggerStyles(item);

				if (!hasChildren) {
					return (
						<Button
							key={item.title}
							{...buttonStyles}
							bg="transparent"
							_hover={{ bg: 'transparent' }}
							_active={{ bg: 'transparent' }}
							textTransform="capitalize"
							whiteSpace={isMidViewport ? 'normal' : 'nowrap'}
							textAlign={isMidViewport ? 'center' : 'left'}
							onClick={() => handleNavigate(item.href)}
							{...(isBVNavItem && { ref: bvNavItemRef })}
						>
							{renderTitle(item.title)}
						</Button>
					);
				}

				// Get parent permission for submenu items
				const parentPermission = getNavItemPermission(item);

				const addIdsToMenuItems = (
					menuItems: typeof item.submenu,
					parentPerm: string | null = null,
				): typeof item.submenu => {
					return menuItems?.map((subItem) => {
						return {
							...subItem,
							// Use href as id for star checking (href is unique identifier)
							id: subItem.href || undefined,
							submenu: subItem.submenu
								? addIdsToMenuItems(
										subItem.submenu,
										getNavItemPermissionWithParent(subItem, parentPerm),
									)
								: undefined,
						};
					});
				};

				return (
					<Box
						key={item.title}
						display="inline-block"
						position="relative"
						onMouseEnter={cancelClose}
						onMouseLeave={() => {
							// Don't close menu automatically during tutorials
							if (!isTourActive) {
								scheduleClose(() =>
									setOpenMenuWithTutorialCheck(null),
								);
							}
						}}
					>
						<FaceliftMenu
							showStars={true}
							isStarred={(id) => {
								// id is the href string
								return id ? isStarred(id) : false;
							}}
							onStarClick={async (menuItem, e) => {
								e.preventDefault();
								e.stopPropagation();
								// Use href as unique identifier and get permission for backend
								if (menuItem.href) {
									// Use parent permission if item doesn't have its own
									const permission = getNavItemPermissionWithParent(
										menuItem,
										parentPermission,
									);
									if (permission) {
										setIsTogglingQuickAction(true);
										setIsSucceed(false);
										setHasError(false);
										setErrorMessage('');

										try {
											await toggleQuickAction(menuItem.href, permission);
											setIsTogglingQuickAction(false);
											setIsSucceed(true);
										} catch (error) {
											setIsTogglingQuickAction(false);
											setHasError(true);
											setErrorMessage(
												error instanceof Error
													? error.message
													: 'Failed to update quick actions',
											);
										}
									}
								}
							}}
							trigger={
								<Box
									as="div"
									{...menuTriggerStyles}
									display="inline-flex"
									alignItems="center"
									px={isCollapsed ? '2' : '3'}
									py="2"
									borderRadius="md"
									color={textColor}
									fontWeight={600}
									fontSize="sm"
									cursor="pointer"
									transition="all 0.3s ease-in-out"
									minW={isCollapsed ? '10' : 'auto'}
									bg="transparent"
									_hover={{ bg: 'transparent' }}
									_active={{ bg: 'transparent' }}
									ref={(el: HTMLElement | null) => {
										if (item.title === 'Orders') {
											ordersNavItemRef.current = el;
											setOrdersNavItemRef(el);
										} else if (item.title === 'Accounting & Finance') {
											accountingFinanceNavItemRef.current = el;
											setAccountingFinanceNavItemRef(el);
										} else if (item.title === 'Invoices') {
											invoicesNavItemRef.current = el;
											setInvoicesNavItemRef(el);
										}
									}}
									onMouseEnter={() => {
										cancelClose();
										if (!isTourActive) {
											setOpenMenu(item.title);
										}
									}}
									onMouseLeave={() => {
										// Don't close menu automatically during tutorials
										if (!isTourActive) {
											scheduleClose(() =>
												setOpenMenuWithTutorialCheck(null),
											);
										}
									}}
								>
									<Box
										as="span"
										whiteSpace={
											isMidViewport && !isCollapsed ? 'normal' : 'nowrap'
										}
										textAlign={
											isMidViewport && !isCollapsed ? 'center' : 'left'
										}
									>
										{renderTitle(item.title)}
									</Box>
									<Show when={!isCollapsed}>
										<Chevron rotated={openMenu === item.title} />
									</Show>
								</Box>
							}
							items={
								addIdsToMenuItems(
									item.submenu?.filter(
										(el) =>
											!el?.permissions ||
											el?.permissions?.length === 0 ||
											hasPermission(el.permissions || []),
									) || [],
								) || []
							}
							open={openMenu === item.title}
							onOpenChange={(open) => {
								if (isTourActive) {
									return;
								}
								setOpenMenu(open ? item.title : null);
							}}
							onMouseEnter={cancelClose}
							onMouseLeave={() => {
								if (!isTourActive) {
									scheduleClose(() =>
										setOpenMenuWithTutorialCheck(null),
									);
								}
							}}
							onNavigate={handleNavigate}
							onSubmenuItemRef={handleSubmenuItemRef}
							expandSubmenuForTour={
								item.title === 'Accounting & Finance'
									? tourExpandSubmenu
									: null
							}
						/>
					</Box>
				);
			})}

			{/* Quick Action Toggle Modal */}
			<FaceliftSuccessProgressModal
				open={
					isTogglingQuickAction || isSucceed || hasError
				}
				isLoading={isTogglingQuickAction}
				isSucceed={isSucceed}
				hasError={hasError}
				onOpenChange={(open: boolean) => {
					if (!open) {
						setIsTogglingQuickAction(false);
						setIsSucceed(false);
						setHasError(false);
						setErrorMessage('');
					}
				}}
				title="Quick Action Updated!"
				errorMessage={errorMessage}
			/>
		</Flex>
	);
};

export default NavLinks;
